import {
	buildPortfolioTrend,
	getEffectiveTransactionDate,
	getSignedCashFlow,
	humanizeAssetLabel,
	normalizeAsset,
	normalizeDateKey,
} from "./shared";

const DAY_IN_MS = 1000 * 60 * 60 * 24;
const DAYS_IN_YEAR = 365;

// Short, locale-independent month names. `toLocaleString` would drift with the
// runtime's ICU data, and these labels sit next to fixed-width chart columns.
const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

// Below this the geometric annualisation exponent (365 / days) gets large enough
// that a week of noise reads as a triple-digit yearly rate. Callers are handed
// the raw cumulative figure instead, flagged so the UI can drop the "/yr".
const MIN_DAYS_TO_ANNUALIZE = 30;

// Herfindahl-Hirschman cutoffs borrowed from the DOJ/FTC merger guidelines,
// where a market scoring below 1500 is unconcentrated, 1500-2500 moderately
// concentrated and above 2500 highly concentrated. The index is scale-free --
// it only ever sees percentage weights -- so the same thresholds carry over to a
// portfolio unchanged. As a sanity check on the register: 1500 is roughly seven
// equal positions, 2500 is exactly four.
const HHI_DIVERSIFIED_MAX = 1500;
const HHI_MODERATE_MAX = 2500;

// A pace within 5% of the goal's requirement is noise, not a trend: one trade
// landing a day either side of a month boundary moves the average by more.
const PACE_TOLERANCE = 0.05;

const toDate = (value) => {
	if (!value) return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const toFiniteNumber = (value) => {
	const number = Number(value);
	return Number.isFinite(number) ? number : 0;
};

const median = (values) => {
	if (!values.length) return 0;
	const sorted = [...values].sort((left, right) => left - right);
	const middle = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0
		? (sorted[middle - 1] + sorted[middle]) / 2
		: sorted[middle];
};

const daysBetween = (from, to) => (to.getTime() - from.getTime()) / DAY_IN_MS;

// A dividend is dated by when it was PAID, not by when the row was entered.
// getEffectiveTransactionDate prefers `createdAt`, which for a dividend keyed in
// months after the fact is the data-entry day -- it would put the income in the
// wrong sub-period and, for a backfilled year, the wrong year entirely. The rest
// of the app (buildInvestmentYears) already reads `date`; this keeps analytics
// agreeing with it.
const getDividendDate = (dividend) =>
	toDate(dividend?.date) || getEffectiveTransactionDate(dividend);

/**
 * Net present value of a dated cash flow set at a given rate, discounted on a
 * 365-day year from the earliest flow.
 *
 * @param {{ years: number, amount: number }[]} flows
 * @param {number} rate annual rate as a decimal fraction
 * @returns {number} NPV, or NaN if the rate makes the discount factor undefined
 */
const netPresentValue = (flows, rate) => {
	const base = 1 + rate;
	if (base <= 0) return NaN;

	let total = 0;
	for (const flow of flows) {
		total += flow.amount / Math.pow(base, flow.years);
	}
	return total;
};

/**
 * Money-weighted (internal) rate of return for an irregular, dated cash flow set.
 *
 * NOTE ON UNITS: this is the one function in this module that returns a DECIMAL
 * FRACTION -- 0.328 means 32.8%. Every other export returns percent numbers
 * (32.8). `buildMoneyWeightedReturn` does the conversion for UI callers.
 *
 * @param {{ date: Date|string, amount: number }[]} cashflows negative = money
 *   into the portfolio, positive = money out of it (including the terminal value)
 * @param {{ lowerBound?: number, upperBound?: number, tolerance?: number, maxIterations?: number }} [options]
 * @returns {number|null} annualised IRR as a decimal fraction, or null when
 *   unsolvable (fewer than two flows, one-sided flows, a single date, or no
 *   sign change inside the bracket). Never NaN, never Infinity.
 */
export const xirr = (cashflows = [], options = {}) => {
	const {
		lowerBound = -0.9999,
		upperBound = 10,
		tolerance = 1e-7,
		maxIterations = 200,
	} = options;

	const parsed = (cashflows || [])
		.map((flow) => ({
			date: toDate(flow?.date),
			amount: toFiniteNumber(flow?.amount),
		}))
		.filter((flow) => flow.date)
		.sort((left, right) => left.date - right.date);

	if (parsed.length < 2) return null;

	const hasInflow = parsed.some((flow) => flow.amount > 0);
	const hasOutflow = parsed.some((flow) => flow.amount < 0);
	// One-sided flows have no rate that zeroes the NPV: the discounted sum keeps
	// the sign of its terms no matter how hard it is discounted.
	if (!hasInflow || !hasOutflow) return null;

	const start = parsed[0].date;
	const end = parsed[parsed.length - 1].date;
	// Every flow on one day collapses the NPV to a constant, so there is no rate
	// to solve for -- and dividing by a zero elapsed period would yield Infinity.
	if (daysBetween(start, end) <= 0) return null;

	const flows = parsed.map((flow) => ({
		amount: flow.amount,
		years: daysBetween(start, flow.date) / DAYS_IN_YEAR,
	}));

	let low = lowerBound;
	let high = upperBound;
	let lowValue = netPresentValue(flows, low);
	let highValue = netPresentValue(flows, high);

	if (!Number.isFinite(lowValue) || !Number.isFinite(highValue)) return null;
	if (Math.abs(lowValue) < tolerance) return low;
	if (Math.abs(highValue) < tolerance) return high;
	// Bisection needs a bracketed root. Newton-Raphson would happily run off
	// towards rate <= -1 here, where the discount factor is undefined, and return
	// NaN for cash flow sets that in fact have a perfectly good solution.
	if (lowValue * highValue > 0) return null;

	let guess = null;
	for (let iteration = 0; iteration < maxIterations; iteration += 1) {
		guess = (low + high) / 2;
		const value = netPresentValue(flows, guess);
		if (!Number.isFinite(value)) return null;
		if (Math.abs(value) < tolerance || (high - low) / 2 < tolerance) {
			return guess;
		}
		if (value * lowValue < 0) {
			high = guess;
			highValue = value;
		} else {
			low = guess;
			lowValue = value;
		}
	}

	return Number.isFinite(guess) ? guess : null;
};

/**
 * Money-weighted return for the whole portfolio: every trade and dividend as a
 * dated cash flow, with today's mark-to-market worth as the terminal inflow.
 *
 * @param {{ investments?: object[], dividends?: object[], currentWorth?: number, now?: Date }} input
 * @returns {{ percent: number|null, isEstimate: boolean, flowCount: number, firstFlowDate: Date|null }}
 *   `percent` is a PERCENT number (32.8, not 0.328) or null when unsolvable.
 */
export const buildMoneyWeightedReturn = ({
	investments = [],
	dividends = [],
	currentWorth = 0,
	now = new Date(),
} = {}) => {
	const asOf = toDate(now) || new Date();
	const worth = toFiniteNumber(currentWorth);
	const flows = [];

	(investments || []).forEach((investment) => {
		const date = getEffectiveTransactionDate(investment);
		// A buy is cash leaving the investor's pocket, so it enters the IRR with
		// the opposite sign to the portfolio-side cash flow shared.js reports.
		const amount = -getSignedCashFlow(investment);
		if (!date || !amount) return;
		flows.push({ date, amount });
	});

	(dividends || []).forEach((dividend) => {
		const date = getDividendDate(dividend);
		const amount = toFiniteNumber(dividend?.amount);
		if (!date || !amount) return;
		flows.push({ date, amount });
	});

	if (worth) flows.push({ date: asOf, amount: worth });

	const sorted = [...flows].sort((left, right) => left.date - right.date);
	const rate = xirr(sorted);

	return {
		percent: rate === null ? null : rate * 100,
		// The terminal flow is a valuation, not a realised sale: the rate is only
		// as good as today's quotes while any position is still open.
		isEstimate: worth > 0,
		flowCount: sorted.length,
		firstFlowDate: sorted.length ? sorted[0].date : null,
	};
};

/**
 * Time-weighted return: the daily sub-period returns chained together, so the
 * size and timing of contributions cannot flatter or penalise the rate. This is
 * the figure to compare against an index; `buildMoneyWeightedReturn` is the
 * figure that answers "what did MY money earn".
 *
 * factor_t = (V_t + D_t) / (V_{t-1} + CF_t)
 *
 * @param {{ investments?: object[], daily?: object[], dividends?: object[], now?: Date }} input
 * @returns {{ cumulativePercent: number, annualizedPercent: number, days: number, stepCount: number, isAnnualized: boolean }}
 *   both figures as PERCENT numbers. Zeroed (never null, never NaN) for an
 *   empty portfolio.
 */
export const buildTimeWeightedReturn = ({
	investments = [],
	daily = [],
	dividends = [],
	now = new Date(),
} = {}) => {
	const empty = {
		cumulativePercent: 0,
		annualizedPercent: 0,
		days: 0,
		stepCount: 0,
		isAnnualized: false,
	};

	const asOfKey = normalizeDateKey(toDate(now) || new Date());
	const trend = buildPortfolioTrend({ investments, daily });
	// A quote row dated in the future would otherwise open a sub-period nobody
	// has lived through yet and stretch the annualisation window with it.
	const lastIndex = trend.timeline.reduce(
		(index, dateKey, position) =>
			!asOfKey || dateKey <= asOfKey ? position : index,
		-1
	);
	if (lastIndex < 1) return empty;

	const timeline = trend.timeline.slice(0, lastIndex + 1);
	const values = trend.totalValuesByDate;
	const invested = trend.investedByDate;

	const dividendFlows = (dividends || [])
		.map((dividend) => ({
			dateKey: normalizeDateKey(getDividendDate(dividend)),
			amount: toFiniteNumber(dividend?.amount),
		}))
		.filter((dividend) => dividend.dateKey && dividend.amount)
		.sort((left, right) => (left.dateKey < right.dateKey ? -1 : 1));

	// A dividend paid on a day with no quote still has to land somewhere: the
	// pointer sweeps it into the first sub-period that closes on or after it,
	// rather than dropping the income because its date missed the timeline.
	let dividendPointer = 0;
	let growth = 1;
	let stepCount = 0;

	for (let index = 1; index <= lastIndex; index += 1) {
		let dividendIncome = 0;
		while (
			dividendPointer < dividendFlows.length &&
			dividendFlows[dividendPointer].dateKey <= timeline[index]
		) {
			dividendIncome += dividendFlows[dividendPointer].amount;
			dividendPointer += 1;
		}

		const openingValue = toFiniteNumber(values[index - 1]);
		const closingValue = toFiniteNumber(values[index]);
		const netFlow = toFiniteNumber(invested[index]) - toFiniteNumber(invested[index - 1]);
		const denominator = openingValue + netFlow;

		// Nothing was at risk over this step (the position opens later, or a sale
		// emptied it), so there is no return to chain. Guarded on <= 0 only: a
		// small positive base is a real, if violent, return and must not vanish.
		if (denominator <= 0) continue;

		growth *= (closingValue + dividendIncome) / denominator;
		stepCount += 1;
	}

	if (!stepCount || !Number.isFinite(growth)) return empty;
	// Total wipeout. Compounding a non-positive growth factor is undefined, so
	// the answer is pinned at -100% rather than allowed to become NaN.
	if (growth <= 0) {
		return {
			cumulativePercent: -100,
			annualizedPercent: -100,
			days: Math.max(0, Math.round(daysBetween(toDate(timeline[0]), toDate(timeline[lastIndex])))),
			stepCount,
			isAnnualized: false,
		};
	}

	const start = toDate(timeline[0]);
	const end = toDate(timeline[lastIndex]);
	const days = start && end ? Math.max(0, daysBetween(start, end)) : 0;
	const cumulativePercent = (growth - 1) * 100;
	const isAnnualized = days >= MIN_DAYS_TO_ANNUALIZE;

	return {
		cumulativePercent,
		annualizedPercent: isAnnualized
			? (Math.pow(growth, DAYS_IN_YEAR / days) - 1) * 100
			: cumulativePercent,
		days: Math.round(days),
		stepCount,
		isAnnualized,
	};
};

/**
 * Concentration profile for the open book.
 *
 * @param {{ asset: string, label?: string, value: number }[]} positions open
 *   positions only; non-positive values are dropped
 * @returns {{ total: number, count: number, weights: { asset: string, label: string, value: number, weight: number }[], topShare: { one: number, three: number, five: number }, hhi: number, effectivePositions: number, level: "DIVERSIFIED"|"MODERATE"|"CONCENTRATED" }}
 *   `weight` and `topShare` are PERCENT numbers (0-100); `hhi` is on the
 *   conventional 0-10000 scale; `effectivePositions` is the count of equally
 *   sized holdings that would score the same HHI.
 */
export const buildConcentration = (positions = []) => {
	const holdings = (positions || [])
		.map((position) => ({
			asset: normalizeAsset(position?.asset),
			label: position?.label || humanizeAssetLabel(position?.asset),
			value: toFiniteNumber(position?.value),
		}))
		.filter((position) => position.value > 0)
		.sort((left, right) => right.value - left.value || left.label.localeCompare(right.label));

	const total = holdings.reduce((sum, position) => sum + position.value, 0);

	if (!holdings.length || total <= 0) {
		return {
			total: 0,
			count: 0,
			weights: [],
			topShare: { one: 0, three: 0, five: 0 },
			hhi: 0,
			effectivePositions: 0,
			level: "DIVERSIFIED",
		};
	}

	const weights = holdings.map((position) => ({
		...position,
		weight: (position.value / total) * 100,
	}));

	const topShare = (count) =>
		weights.slice(0, count).reduce((sum, position) => sum + position.weight, 0);

	const hhi = weights.reduce((sum, position) => sum + position.weight * position.weight, 0);

	return {
		total,
		count: weights.length,
		weights,
		topShare: { one: topShare(1), three: topShare(3), five: topShare(5) },
		hhi,
		// Guarded even though `total > 0` implies a positive HHI: float dust in a
		// single sub-cent position could otherwise square down to zero.
		effectivePositions: hhi > 0 ? 10000 / hhi : 0,
		level:
			hhi < HHI_DIVERSIFIED_MAX
				? "DIVERSIFIED"
				: hhi < HHI_MODERATE_MAX
					? "MODERATE"
					: "CONCENTRATED",
	};
};

/**
 * Which holdings produced the portfolio's gain, biggest winner first.
 *
 * @param {object[]} summaryAssets rows from `GET /api/investments/summary`.assets
 * @returns {{ contributions: { asset: string, label: string, totalReturn: number, unrealizedPL: number, realizedPL: number, dividendIncome: number, sharePercent: number }[], totals: { totalReturn: number, totalPositive: number, totalNegative: number, winners: number, losers: number } }}
 *   `contributions` is sorted by `totalReturn` descending. `sharePercent` is a
 *   PERCENT number; it is negative for losing positions.
 */
export const buildReturnContributions = (summaryAssets = []) => {
	const rows = (summaryAssets || [])
		.map((row) => {
			const asset = normalizeAsset(row?.asset);
			return {
				asset,
				label: humanizeAssetLabel(asset),
				totalReturn: toFiniteNumber(row?.totalReturn),
				unrealizedPL: toFiniteNumber(row?.unrealizedPL),
				realizedPL: toFiniteNumber(row?.realizedPL),
				dividendIncome: toFiniteNumber(row?.dividendIncome),
			};
		})
		.filter((row) => row.asset)
		.sort((left, right) => right.totalReturn - left.totalReturn || left.label.localeCompare(right.label));

	const totalPositive = rows.reduce(
		(sum, row) => sum + (row.totalReturn > 0 ? row.totalReturn : 0),
		0
	);
	const totalNegative = rows.reduce(
		(sum, row) => sum + (row.totalReturn < 0 ? row.totalReturn : 0),
		0
	);
	const totalReturn = totalPositive + totalNegative;

	// The denominator is the gross gain, not the net. Dividing by the net total
	// makes a portfolio whose winners and losers nearly cancel report shares in
	// the thousands of percent -- +900 on a +10 net book is 9000% "of the gain",
	// which is arithmetic, not information. Against the gross gain the winners'
	// shares add to 100 and the losers read as the drag they are.
	const contributions = rows.map((row) => ({
		...row,
		sharePercent: totalPositive > 0 ? (row.totalReturn / totalPositive) * 100 : 0,
	}));

	return {
		contributions,
		totals: {
			totalReturn,
			totalPositive,
			totalNegative,
			winners: rows.filter((row) => row.totalReturn > 0).length,
			losers: rows.filter((row) => row.totalReturn < 0).length,
		},
	};
};

/**
 * Monthly saving pace over a trailing window ending with the current month.
 *
 * @param {{ investments?: object[], months?: number, now?: Date }} input
 * @returns {{ months: { key: string, label: string, buys: number, sells: number, net: number, monthStart: Date, isPartial: boolean }[], averageMonthlyNet: number, medianMonthlyNet: number, monthsWithNoBuys: number, totalNet: number, lastContributionDate: Date|null }}
 *   `months` is oldest-first and always exactly `months` long, zero-activity
 *   months included. `lastContributionDate` looks across all investments, not
 *   just the window, so a dormant portfolio still shows when it last got money.
 */
export const buildContributionPace = ({
	investments = [],
	months = 12,
	now = new Date(),
} = {}) => {
	const windowLength = Math.max(1, Math.floor(toFiniteNumber(months) || 0) || 12);
	const asOf = toDate(now) || new Date();
	const endYear = asOf.getUTCFullYear();
	const endMonth = asOf.getUTCMonth();

	const buckets = new Map();
	const rows = [];
	for (let offset = windowLength - 1; offset >= 0; offset -= 1) {
		const monthStart = new Date(Date.UTC(endYear, endMonth - offset, 1));
		const year = monthStart.getUTCFullYear();
		const month = monthStart.getUTCMonth();
		const row = {
			key: `${year}-${String(month + 1).padStart(2, "0")}`,
			label: `${MONTH_LABELS[month]} ${year}`,
			buys: 0,
			sells: 0,
			net: 0,
			monthStart,
			// The newest bucket is still filling. Without the flag a chart reads a
			// month that is three days old as a collapse in saving.
			isPartial: offset === 0,
		};
		rows.push(row);
		buckets.set(row.key, row);
	}

	let lastContributionDate = null;

	(investments || []).forEach((investment) => {
		const date = getEffectiveTransactionDate(investment);
		if (!date) return;
		const cashFlow = getSignedCashFlow(investment);

		if (cashFlow > 0 && (!lastContributionDate || date > lastContributionDate)) {
			lastContributionDate = date;
		}

		const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
		const row = buckets.get(key);
		if (!row) return;

		if (cashFlow > 0) row.buys += cashFlow;
		else if (cashFlow < 0) row.sells += -cashFlow;
		row.net = row.buys - row.sells;
	});

	const nets = rows.map((row) => row.net);
	const totalNet = nets.reduce((sum, net) => sum + net, 0);

	return {
		months: rows,
		averageMonthlyNet: totalNet / windowLength,
		medianMonthlyNet: median(nets),
		monthsWithNoBuys: rows.filter((row) => row.buys === 0).length,
		totalNet,
		lastContributionDate,
	};
};

/**
 * The saving pace measured against a goal's required monthly contribution.
 *
 * @param {{ investments?: object[], requiredMonthly?: number, months?: number, now?: Date }} input
 * @returns {object} everything `buildContributionPace` returns, plus
 *   `{ requiredMonthly, paceRatio, surplus, status }`. `paceRatio` is a plain
 *   ratio (1.2 = 20% ahead), NOT a percent, and is null when nothing is
 *   required. `status` is "AHEAD" | "ON_TRACK" | "BEHIND".
 */
export const buildSavingPace = ({
	investments = [],
	requiredMonthly = 0,
	months = 12,
	now = new Date(),
} = {}) => {
	const pace = buildContributionPace({ investments, months, now });
	const required = toFiniteNumber(requiredMonthly);

	// Nothing left to save for: the goal is already funded, so any pace clears it
	// and a ratio against zero would be Infinity.
	if (required <= 0) {
		return {
			...pace,
			requiredMonthly: 0,
			paceRatio: null,
			surplus: pace.averageMonthlyNet,
			status: "AHEAD",
		};
	}

	const paceRatio = pace.averageMonthlyNet / required;

	return {
		...pace,
		requiredMonthly: required,
		paceRatio,
		surplus: pace.averageMonthlyNet - required,
		status:
			paceRatio >= 1 + PACE_TOLERANCE
				? "AHEAD"
				: paceRatio >= 1 - PACE_TOLERANCE
					? "ON_TRACK"
					: "BEHIND",
	};
};
